<template>
  <div class="admin-shell">
    <section class="admin-panel">
      <div class="admin-header">
        <div>
          <p class="eyebrow">Wine Bar Admin</p>
          <h1>User Activation</h1>
          <p class="subcopy">
            Activate registered users and manage basic role access.
          </p>
        </div>

        <button type="button" class="refresh-btn" @click="loadUsers">
          Refresh
        </button>
      </div>

      <div v-if="errorMessage" class="message-card error-card">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="message-card success-card">
        {{ successMessage }}
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <span>Pending</span>
          <strong>{{ pendingUsers.length }}</strong>
        </div>

        <div class="summary-card">
          <span>Active</span>
          <strong>{{ activeUsers.length }}</strong>
        </div>

        <div class="summary-card">
          <span>Total Users</span>
          <strong>{{ users.length }}</strong>
        </div>
      </div>

      <div class="section-block">
        <div class="section-heading">
          <h2>Pending Activation</h2>
          <p>New registrations appear here until an admin activates them.</p>
        </div>

        <div v-if="pendingUsers.length === 0" class="empty-state">
          No pending users.
        </div>

        <div v-else class="user-list">
          <article
            v-for="user in pendingUsers"
            :key="user.user_id"
            class="user-card pending"
          >
            <div class="user-main">
              <h3>{{ user.username }}</h3>
              <p>Registered: {{ formatDate(user.created_at) }}</p>
              <small>2FA: {{ user.has_totp ? "Configured" : "Not configured" }}</small>
            </div>

            <div class="role-picker">
              <label>
                <span>Role</span>
                <select v-model="roleSelections[user.user_id]">
                  <option value="cashier">cashier</option>
                  <option value="manager">manager</option>
                  <option value="admin">admin</option>
                </select>
              </label>
            </div>

            <div class="actions">
              <button
                type="button"
                class="secondary-action"
                @click="saveRole(user)"
              >
                Save Role
              </button>

              <button
                type="button"
                class="primary-action"
                @click="activateUser(user)"
              >
                Activate
              </button>

              <button
                type="button"
                class="secondary-action"
                @click="resetPassword(user)"
              >
                Reset Password
              </button>

              <button
                type="button"
                class="danger-action"
                @click="deleteUser(user)"
              >
                Delete
              </button>
            </div>
          </article>
        </div>
      </div>

      <div class="section-block">
        <div class="section-heading">
          <h2>Active Users</h2>
          <p>Users who can currently log in.</p>
        </div>

        <div v-if="activeUsers.length === 0" class="empty-state">
          No active users.
        </div>

        <div v-else class="user-list">
          <article
            v-for="user in activeUsers"
            :key="user.user_id"
            class="user-card"
          >
            <div class="user-main">
              <h3>{{ user.username }}</h3>
              <p>Role: {{ user.role }}</p>
              <small>2FA: {{ user.has_totp ? "Configured" : "Not configured" }}</small>
            </div>

            <div class="role-picker">
              <label>
                <span>Role</span>
                <select v-model="roleSelections[user.user_id]">
                  <option value="cashier">cashier</option>
                  <option value="manager">manager</option>
                  <option value="admin">admin</option>
                </select>
              </label>
            </div>

            <div class="actions">
              <button
                type="button"
                class="secondary-action"
                @click="saveRole(user)"
              >
                Save Role
              </button>

              <button
                type="button"
                class="danger-action"
                @click="deactivateUser(user)"
              >
                Deactivate
              </button>
            </div>
          </article>
        </div>
      </div>

      <div class="section-block">
        <div class="section-heading">
          <h2>Role Permissions</h2>
          <p>Choose what each role is allowed to access.</p>
        </div>

        <div v-if="roles.length === 0 || permissions.length === 0" class="empty-state">
          No roles or permissions found.
        </div>

        <div v-else class="permission-grid">
          <article
            v-for="role in roles"
            :key="role.role_id"
            class="permission-card"
          >
            <div class="permission-card-header">
              <div>
                <h3>{{ role.role_name }}</h3>
                <p>{{ role.description || "No description" }}</p>
              </div>

              <button
                type="button"
                class="primary-action"
                @click="saveRolePermissions(role)"
                :disabled="role.role_name === 'admin'"
              >
                Save
              </button>
            </div>

            <div
              v-if="role.role_name === 'admin'"
              class="admin-lock-note"
            >
              Admin has all permissions and cannot be edited here.
            </div>

            <div class="permission-list">
              <label
                v-for="permission in permissions"
                :key="permission.perm_id"
                class="permission-row"
                :class="{ disabled: role.role_name === 'admin' }"
              >
                <input
                  type="checkbox"
                  :value="permission.perm_key"
                  v-model="rolePermissionSelections[role.role_name]"
                  :disabled="role.role_name === 'admin'"
                />

                <span>
                  <strong>{{ permission.perm_key }}</strong>
                  <small>{{ permission.description }}</small>
                </span>
              </label>
            </div>
          </article>
        </div>
      </div>

    </section>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "AdminPage",

  data() {
    return {
      users: [],
      roleSelections: {},

      roles: [],
      permissions: [],
      rolePermissionSelections: {},

      errorMessage: "",
      successMessage: "",


    };
  },

  computed: {
    pendingUsers() {
      return this.users.filter(user => !user.is_active);
    },

    activeUsers() {
      return this.users.filter(user => user.is_active);
    }
  },

  mounted() {
    this.loadUsers();
  },

  methods: {
    resetMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },

    async loadUsers() {
      this.resetMessages();

      try {
        const response = await api.get("/admin/users");

        this.users = response.data.users || [];

        const selections = {};
        this.users.forEach(user => {
          selections[user.user_id] = user.role || "cashier";
        });

        this.roleSelections = selections;

        await this.loadRolePermissions();

      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to load users.";
      }
    },

    async activateUser(user) {
      this.resetMessages();

      try {
        await this.saveRole(user, false);

        await api.patch(`/admin/users/${user.user_id}/active`, {
          is_active: true
        });

        this.successMessage = `${user.username} has been activated.`;

        await this.loadUsers();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to activate user.";
      }
    },

    async deactivateUser(user) {
      this.resetMessages();

      const confirmed = window.confirm(
        `Deactivate ${user.username}? They will no longer be able to log in.`
      );

      if (!confirmed) return;

      try {
        await api.patch(`/admin/users/${user.user_id}/active`, {
          is_active: false
        });

        this.successMessage = `${user.username} has been deactivated.`;

        await this.loadUsers();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to deactivate user.";
      }
    },

    async resetPassword(user) {
      this.resetMessages();

      const newPassword = window.prompt(
        `Enter a new temporary password for ${user.username}:`
      );

      if (!newPassword) return;

      try {
        await api.put(`/admin/users/${user.user_id}/password`, {
          password: newPassword
        });

        this.successMessage = `Password reset for ${user.username}.`;
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to reset password.";
      }
    },

    async deleteUser(user) {
      this.resetMessages();

      const confirmed = window.confirm(
        `Permanently delete ${user.username}? This cannot be undone.`
      );

      if (!confirmed) return;

      try {
        await api.delete(`/admin/users/${user.user_id}`);

        this.successMessage = `${user.username} has been deleted.`;

        await this.loadUsers();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to delete user.";
      }
    },

    async saveRole(user, showMessage = true) {
      const selectedRole = this.roleSelections[user.user_id] || "cashier";

      await api.patch(`/admin/users/${user.user_id}/role`, {
        role: selectedRole
      });

      if (showMessage) {
        this.successMessage = `Role updated for ${user.username}.`;
        await this.loadUsers();
      }
    },

    async loadRolePermissions() {
      try {
        const response = await api.get("/admin/role-permissions");

        this.roles = response.data.roles || [];
        this.permissions = response.data.permissions || [];

        const selections = {};

        this.roles.forEach(role => {
          selections[role.role_name] = [];
        });

        (response.data.rolePermissions || []).forEach(row => {
          if (!selections[row.role_name]) {
            selections[row.role_name] = [];
          }

          selections[row.role_name].push(row.perm_key);
        });

        this.rolePermissionSelections = selections;
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to load role permissions.";
      }
    },

    async saveRolePermissions(role) {
      this.resetMessages();

      const selectedPermissions =
        this.rolePermissionSelections[role.role_name] || [];

      try {
        await api.put(`/admin/roles/${role.role_name}/permissions`, {
          permissions: selectedPermissions
        });

        this.successMessage = `Permissions updated for ${role.role_name}.`;

        await this.loadRolePermissions();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Failed to update role permissions.";
      }
    },

    formatDate(value) {
      if (!value) return "Unknown";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "Unknown";
      }

      return date.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.admin-shell {
  min-height: 100vh;
  padding: 32px;
  color: #fff8f1;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.28), transparent 34%),
    radial-gradient(circle at bottom right, rgba(200, 166, 106, 0.12), transparent 28%),
    linear-gradient(135deg, #120b10, #22131a 48%, #0f0a0d);
}

.admin-panel {
  width: min(1180px, 100%);
  margin: 0 auto;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.095), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.105);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.25);
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.admin-header h1 {
  margin: 0;
  font-size: clamp(42px, 6vw, 72px);
  line-height: 0.9;
  letter-spacing: -0.075em;
}

.subcopy {
  max-width: 620px;
  margin: 14px 0 0;
  color: #d8cbc4;
  font-size: 16px;
  line-height: 1.5;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  padding: 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(255, 255, 255, 0.095);
}

.summary-card span {
  display: block;
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 42px;
  line-height: 1;
  color: #f1c979;
}

.section-block {
  margin-bottom: 24px;
  padding: 24px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.095);
}

.section-heading {
  margin-bottom: 18px;
}

.section-heading h2 {
  margin: 0;
  font-size: 30px;
  letter-spacing: -0.04em;
}

.section-heading p {
  margin: 8px 0 0;
  color: #cfc2bd;
}

.user-list {
  display: grid;
  gap: 14px;
}

.user-card {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 220px auto;
  gap: 18px;
  align-items: center;
  padding: 18px;
  border-radius: 22px;
  background: rgba(8, 5, 8, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.085);
}

.user-card.pending {
  border-color: rgba(241, 201, 121, 0.28);
}

.user-main h3 {
  margin: 0;
  font-size: 20px;
}

.user-main p {
  margin: 4px 0;
  color: #d8cbc4;
}

.user-main small {
  color: #b9aaa4;
}

.role-picker label {
  display: grid;
  gap: 8px;
}

.role-picker span {
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

select {
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(8, 5, 8, 0.72);
  color: #fff8f1;
  font-family: inherit;
  outline: none;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.primary-action,
.secondary-action,
.danger-action,
.refresh-btn {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 900;
  border: 0;
}

.primary-action {
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
}

.secondary-action {
  color: #fff8f1;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.danger-action {
  color: #ffd8d1;
  background: rgba(255, 83, 73, 0.16);
  border: 1px solid rgba(255, 142, 122, 0.26);
}

.refresh-btn {
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
  white-space: nowrap;
}

.message-card {
  margin-bottom: 16px;
  padding: 13px 14px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
}

.error-card {
  color: #ffd8d1;
  background: rgba(255, 83, 73, 0.13);
  border: 1px solid rgba(255, 142, 122, 0.26);
}

.success-card {
  color: #d9ffe8;
  background: rgba(78, 191, 126, 0.13);
  border: 1px solid rgba(120, 224, 160, 0.25);
}

.empty-state {
  padding: 18px;
  border-radius: 18px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px dashed rgba(255, 255, 255, 0.14);
}

.permission-grid {
  display: grid;
  gap: 18px;
}

.permission-card {
  padding: 18px;
  border-radius: 22px;
  background: rgba(8, 5, 8, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.085);
}

.permission-card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.permission-card-header h3 {
  margin: 0;
  font-size: 22px;
  text-transform: capitalize;
}

.permission-card-header p {
  margin: 6px 0 0;
  color: #cfc2bd;
}

.permission-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
}

.permission-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
}

.permission-row input {
  width: auto;
  min-height: auto;
  margin-top: 3px;
}

.permission-row span {
  display: grid;
  gap: 4px;
}

.permission-row strong {
  color: #fff8f1;
  font-size: 14px;
}

.permission-row small {
  color: #b9aaa4;
  line-height: 1.35;
}

.permission-row.disabled {
  opacity: 0.62;
  cursor: not-allowed;
}

.admin-lock-note {
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  color: #f1c979;
  background: rgba(241, 201, 121, 0.1);
  border: 1px solid rgba(241, 201, 121, 0.18);
  font-weight: 800;
}

@media (max-width: 920px) {
  .admin-header {
    display: grid;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .user-card {
    grid-template-columns: 1fr;
  }

  .actions {
    justify-content: flex-start;
  }
}
</style>